import parser from 'parse-address';

export const formatAddress = (
    address1: string,
    address2: string | undefined | null,
    city: string,
    state: string,
    zip: string
) => ([address1, address2, city, state].filter((val): val is string => !!val).join(', ') + ` ${zip}`).trim();

/**
 * Address validation interface
 *
 * @typedef AddressValidation
 * @property {boolean} isValidFormal - address is complete and correct
 * @property {boolean} isValidInformal - address meets less strict completeness guidelines, allowing for variations in positioning of street address values
 * @property {boolean} hasNumber - does the street address have a number
 * @property {boolean} hasStreet - does the address have a street name
 * @property {boolean} isValidAddress - is the address generally valid - either formal or (informal with a number and street name)
 *
 */
export interface AddressValidation {
    /**
     * Address is complete, with specific street address structure, city, state and zip
     */
    isValidFormal: boolean;
    /**
     * Address meets less strict completeness guidelines, allowing for variations in positioning of street address values
     */
    isValidInformal: boolean;
    /**
     * Does the street address have a number
     */
    hasNumber: boolean;
    /**
     * Does the address have a street name
     */
    hasStreet: boolean;
    /**
     * Is the address generally valid - either formal or (informal with a number and street name)
     */
    isValidAddress: boolean;
}

export const addressValidator = (
    address1: string,
    address2: string,
    city: string,
    state: string,
    zip: string
): AddressValidation => {
    const formattedAddress = formatAddress(address1, address2, city, state, zip);
    const formalLocation = parser.parseAddress(formattedAddress);
    const isValidFormal = !!city.length && !!state.length && !!zip.length && !!formalLocation;

    const informalLocation = parser.parseInformalAddress(address1); // VERY INFORMAL - Street Address Only!
    const isValidInformal = !!informalLocation;

    // address1 must contain at least two words to appear valid
    const isAddr1MultiWord = address1.trim().split(/\s+/).length > 1;

    const hasNumber = !!formalLocation?.number ?? !!informalLocation?.number;
    const hasStreet = isAddr1MultiWord && (!!formalLocation?.street || !!informalLocation?.street);
    return {
        isValidFormal,
        isValidInformal,
        hasNumber,
        hasStreet,
        isValidAddress: isValidFormal || (isValidInformal && hasNumber && hasStreet),
    };
};
